<template>
  <td
    v-for="col in columns"
    :key="col.id"
    :data-col-id="col.id"
    :class="cellClass(col)"
    @mousedown="onNoteCellMouseDown(col, $event)"
    @click="onCellClick(col, $event)"
  >
    <template v-if="col.isMultiedit">
      <div class="qtl-multi">
        <mosta-checkbox
          class="qtl-multi__check"
          :model-value="isChecked"
          @update:model-value="onToggleCheckbox"
          @click.stop
        />
        <span class="qtl-row-num">{{ (rowNumberBase ?? 0) + rowIndex + 1 }}.</span>
        <!-- Урна удаления позиции: только в выборке по складским позициям и при праве
             на запись в склад. Видна при наведении на строку. -->
        <button
          v-if="isStoragePositions"
          type="button"
          class="qtl-multi__del"
          :disabled="!storageWA"
          :aria-label="$t('remove')"
          @click.stop="openDeleteConfirm"
        >
          <span class="qtl-multi__del-ico"></span>
        </button>
      </div>
    </template>

    <template v-else-if="row.cells[col.id] && row.cells[col.id].subItems.length">
      <template v-for="(sub, subIdx) in row.cells[col.id].subItems" :key="subIdx">
        <span
          v-if="row.cells[col.id].subItems.length > 1 && sub.Data && sub.Data.length && headerPartAt(col.text, subIdx)"
          class="qtl-sub-header"
          v-html="headerPartAt(col.text, subIdx)"
        />

        <MinimizedCellLite :minimized="isMinimized(sub)" :count="sub.Data ? sub.Data.length : 0">
        <template v-if="(sub.DisplayType === 0 || sub.DisplayType === 6) && !isHeavySimple(sub)">
          <SimpleCellElementLite
            :element="sub"
            :entity-id="row.cells[col.id].entityId"
            :creator-id="row.cells[col.id].creatorId"
            @refreshNeeded="$emit('refreshNeeded')"
          />
        </template>

        <ChangeDateCellElementLite
          v-else-if="sub.DisplayType === 7"
          :element="sub"
        />

        <span
          v-else-if="sub.DisplayType === 12 || sub.DisplayType === 15"
          class="qtl-icon-cell"
          v-html="iconHtml(sub)"
        />

        <BillCellElementLite
          v-else-if="sub.DisplayType === 18"
          :element="sub"
        />

        <!-- DisplayType 16 (DataOfStorageItem) рекурсивно разворачивается в подэлементы
             разных типов (параметры, simple, иконки и т.п.). Рендерим полностью на
             новых lite-компонентах через CommonCellElementLite, подсветку параметров
             пробрасываем теми же hover-props, что и обычные параметрические ячейки. -->
        <CommonCellElementLite
          v-else-if="sub.DisplayType === 16"
          :element="sub"
          :src-element="srcElement"
          :entity-id="row.cells[col.id].entityId"
          :creator-id="row.cells[col.id].creatorId"
          :WA="WA"
          :column-index="col.id + '_' + subIdx"
          :hovered-param-rec-id="hoveredParamRecId"
          :no-row-height-align="row.cells[col.id].subItems.length > 1"
          @changeHoverRecord="onCellHoverRecord"
          @changeHeights="$emit('changeHeights', $event)"
          @refreshNeeded="$emit('refreshNeeded')"
          @sourceItemsRequested="$emit('sourceItemsRequested', { ...$event, entityId: row.cells[col.id].entityId })"
        />

        <EntityCellElementLite
          v-else-if="[1, 3, 4, 8, 11, 13, 14, 17].includes(sub.DisplayType)"
          :element="sub"
        />

        <JobCellElementLite
          v-else-if="sub.DisplayType === 5 || sub.DisplayType === 9"
          :element="sub"
        />

        <TagCellElementLite
          v-else-if="sub.DisplayType === 10"
          :element="sub"
          :entity-id="row.cells[col.id].entityId"
          @refreshNeeded="$emit('refreshNeeded')"
        />

        <SourceItemsCellElementLite
          v-else-if="sub.DisplayType === 19"
          :element="sub"
          @sourceItemsRequested="$emit('sourceItemsRequested', { ...$event, entityId: row.cells[col.id].entityId })"
        />

        <ParameterCellElementLite
          v-else-if="sub.DisplayType === 2"
          :element="sub"
          :table-row-number="row.cells[col.id].entityId"
          :column-index="col.id + '_' + subIdx"
          :hovered-param-rec-id="hoveredParamRecId"
          @changeHoverRecord="onCellHoverRecord"
          @changeHeights="$emit('changeHeights', $event)"
        />

        <TagNameCellElementLite
          v-else-if="isTagName(sub)"
          :element="sub"
          :entity-id="row.cells[col.id].entityId"
          @refreshNeeded="$emit('refreshNeeded')"
        />

        <VendorCodeCellElementLite
          v-else-if="isVendorCode(sub)"
          :element="sub"
          :entity-id="row.cells[col.id].entityId"
        />

        <NotesCellElementLite
          v-else-if="isNote(sub) || isTableComment(sub)"
          :ref="(inst: any) => setNoteRef(col.id, inst)"
          :element="sub"
          :entity-id="row.cells[col.id].entityId"
          :creator-id="row.cells[col.id].creatorId"
        />

        <!-- Резервная ветка: все известные типы данных обрабатываются выше, сюда
             попадают только нераспознанные ячейки, их значения рисует
             SimpleCellElementLite. -->
        <SimpleCellElementLite
          v-else
          :element="sub"
          :entity-id="row.cells[col.id].entityId"
          :creator-id="row.cells[col.id].creatorId"
          @refreshNeeded="$emit('refreshNeeded')"
        />
        </MinimizedCellLite>
      </template>

      <!-- Карандаш правки контакта: в выборке по контактам стоит инлайн сразу после
           имени и виден только при наведении на строку, см. стили. -->
      <button
        v-if="isPersonNameCell(col)"
        type="button"
        class="qtl-person-edit-btn"
        :aria-label="$t('edit')"
        @click.stop="onEditPersonClick"
      >
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M14.9372 3.83936C15.1701 3.60633 15.1203 3.1787 14.8259 2.8843L13.1148 1.17324C12.8203 0.878802 12.3927 0.828861 12.1597 1.06191L10.7524 2.46913L13.5299 5.24659L14.9372 3.83936Z" fill="currentColor" stroke="currentColor" stroke-width="0.390625"/>
          <path d="M1.00066 15.195C1.64288 15.0543 3.58866 14.546 4.27003 14.3671C4.42665 14.3261 4.56959 14.2439 4.68397 14.1292L12.8352 5.97791L10.0576 3.20032L1.90638 11.3516C1.79163 11.4659 1.7096 11.6089 1.66846 11.7656C1.4895 12.4472 0.981335 14.3934 0.841554 15.0363C0.831666 15.0807 0.845216 15.1271 0.877441 15.1592C0.909666 15.1914 0.956223 15.2048 1.00066 15.195Z" fill="currentColor" stroke="currentColor" stroke-width="0.390625"/>
        </svg>
      </button>
    </template>
  </td>

  <!-- Подтверждение удаления складской позиции - lite-поповер вместо q-menu/q-card. -->
  <mosta-context-menu
    v-if="isStoragePositions"
    :visible="deleteConfirmOpen"
    :position="deleteMenuPos"
    @close="deleteConfirmOpen = false"
  >
    <div class="qtl-del-confirm">
      <div class="qtl-del-confirm__text">{{ $t('sure_delete') }}</div>
      <div class="qtl-del-confirm__actions">
        <button type="button" class="qtl-del-confirm__btn" @click="deleteConfirmOpen = false">
          {{ $t('cancel') }}
        </button>
        <button type="button" class="qtl-del-confirm__btn qtl-del-confirm__btn--accent" @click="confirmDelete">
          {{ $t('remove') }}
        </button>
      </div>
    </div>
  </mosta-context-menu>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import MostaCheckbox from "@/components/common/basic/MostaCheckbox.vue";
import MostaContextMenu from "@/components/common/basic/MostaContextMenu.vue";
import SimpleCellElementLite from "../cell-items-lite/SimpleCellElementLite.vue";
import ChangeDateCellElementLite from "../cell-items-lite/ChangeDateCellElementLite.vue";
import BillCellElementLite from "../cell-items-lite/BillCellElementLite.vue";
import CommonCellElementLite from "../cell-items-lite/CommonCellElementLite.vue";
import EntityCellElementLite from "../cell-items-lite/EntityCellElementLite.vue";
import JobCellElementLite from "../cell-items-lite/JobCellElementLite.vue";
import TagCellElementLite from "../cell-items-lite/TagCellElementLite.vue";
import ParameterCellElementLite from "../cell-items-lite/ParameterCellElementLite.vue";
import TagNameCellElementLite from "../cell-items-lite/TagNameCellElementLite.vue";
import VendorCodeCellElementLite from "../cell-items-lite/VendorCodeCellElementLite.vue";
import NotesCellElementLite from "../cell-items-lite/NotesCellElementLite.vue";
import MinimizedCellLite from "../cell-items-lite/MinimizedCellLite.vue";
import SourceItemsCellElementLite from "../cell-items-lite/SourceItemsCellElementLite.vue";

import type { TableDataCell } from "@/models/interfaces/entity-settings/querytable/querytable";
import {
  AnalyticsV2ElementType,
  ColumnCategory,
  TableDataCellDisplayType,
} from "@/models/etc/types";
import { generalStore } from "@/store/general";
import { storageStore } from "@/store/storage/storage-instances";
import { storagePositionsStore } from "@/store/storage/storage-positions";
import { formatIconHtml } from "../cell-items-lite/cellFormat";
import type { LiteColumn, LiteRow } from "../types";
import { isCommentColumn } from "../commentColumn";

const props = defineProps<{
  row: LiteRow;
  columns: LiteColumn[];
  srcElement: any;
  WA: boolean;
  isChecked: boolean;
  rowIndex: number;
  /** номер первой строки страницы (= pagination offset); нумерация сквозная по страницам */
  rowNumberBase?: number;
}>();

const emit = defineEmits<{
  (e: "toggleCheckbox", payload: { entityId: number; checked: boolean }): void;
  (e: "changeHoverRecord", payload: any): void;
  (e: "changeHeights", payload: any): void;
  (e: "refreshNeeded"): void;
  (e: "updatePerson", payload: { ev: any; entityId: number }): void;
  (e: "updateSelection", payload: { entityId: number; checked: boolean }): void;
  (e: "checkBoxClick"): void;
  (e: "sourceItemsRequested", payload: { ids: number[]; type: number; entityId: number }): void;
}>();

const gstore = generalStore();
const storage = storageStore();
const storagePos = storagePositionsStore();
const hoveredParamRecId = ref<string | number | undefined>(undefined);
const canEditPerson = !!props.WA && gstore.selectedItem?.DestType === AnalyticsV2ElementType.Person;

// Урна удаления позиции показывается только в выборке по складским позициям
// (единообразно с canEditPerson по DestType). Активность гейтим правом записи в склад.
const isStoragePositions = computed(
  () => gstore.selectedItem?.DestType === AnalyticsV2ElementType.StorageItem,
);
const storageWA = computed(() => !!storage.storageAccess?.CanWrite);

const deleteConfirmOpen = ref(false);
const deleteMenuPos = ref<{ top: string; left: string }>({ top: "0px", left: "0px" });

function openDeleteConfirm(ev: MouseEvent) {
  if (!storageWA.value) return;
  const btn = ev.currentTarget as HTMLElement | null;
  const rect = btn?.getBoundingClientRect();
  if (rect) {
    deleteMenuPos.value = { top: `${rect.bottom + 2}px`, left: `${rect.left}px` };
  } else {
    deleteMenuPos.value = { top: `${ev.clientY}px`, left: `${ev.clientX}px` };
  }
  deleteConfirmOpen.value = true;
}

function confirmDelete() {
  // Строка убирается из таблицы внутри стора через queryStore().stripTableRows.
  if (props.row.entityId) storagePos.deleteStorageItem(props.row.entityId);
  deleteConfirmOpen.value = false;
}

function onCellHoverRecord(payload: { recId: any; rowNum: number }) {
  hoveredParamRecId.value = payload?.recId;
  emit("changeHoverRecord", payload);
}

function cellClass(col: LiteColumn) {
  return {
    "qtl-cell": true,
    "qtl-cell--multiedit": !!col.isMultiedit,
    "qtl-cell--note": isNoteColumn(col),
    "qtl-col-comment": isCommentColumn(col),
  };
}

// У ячейки комментария свои отступы внутри NativeInput, а клик по ней включает правку,
// поэтому padding самой td снимаем, чтобы кликабельной была вся площадь.
function isNoteColumn(col: LiteColumn): boolean {
  if (col.isMultiedit) return false;
  const cell = props.row.cells[col.id];
  if (!cell || !cell.subItems) return false;
  return cell.subItems.some((s) => isNote(s) || isTableComment(s));
}

function onToggleCheckbox(val: boolean) {
  emit("toggleCheckbox", { entityId: props.row.entityId, checked: val });
}

// Клик по всей ячейке галочек переключает выбор строки. Сам чекбокс и урна удаления
// с @click.stop, поэтому двойного срабатывания нет.
function onMultieditCellClick(col: LiteColumn) {
  if (!col.isMultiedit) return;
  emit("toggleCheckbox", { entityId: props.row.entityId, checked: !props.isChecked });
}

// Ячейки комментария по колонкам этой строки: ячейку рисуем мы, а правку ведёт сама
// ячейка комментария, поэтому события с td передаём ей. При размонтировании Vue отдаёт
// null, по нему запись убираем, иначе в виртуальном списке останутся мёртвые ссылки.
const noteCells = new Map<string, any>();
function setNoteRef(colId: string, inst: any) {
  if (inst) noteCells.set(colId, inst);
  else noteCells.delete(colId);
}

// Событие уходит в ячейку комментария, только если попали в пустое место ячейки (цель
// это сама td) или внутрь блока комментария. Иначе в ячейке с несколькими подэлементами
// правку открывал бы и клик по постороннему содержимому, например по подзаголовку.
function noteCellFor(col: LiteColumn, ev: MouseEvent): any | null {
  const inst = noteCells.get(col.id);
  if (!inst) return null;
  const target = ev.target as HTMLElement | null;
  if (!target) return null;
  if (target === ev.currentTarget || target.closest(".qtl-note")) return inst;
  return null;
}

function onNoteCellMouseDown(col: LiteColumn, ev: MouseEvent) {
  noteCellFor(col, ev)?.handleCellMouseDown?.(ev);
}

function onCellClick(col: LiteColumn, ev: MouseEvent) {
  onMultieditCellClick(col);
  noteCellFor(col, ev)?.handleCellClick?.(ev);
}

function onEditPersonClick(ev: MouseEvent) {
  emit("updatePerson", { ev, entityId: props.row.entityId });
}

// Ячейка имени контакта: в выборке по контактам (canEditPerson уже проверяет
// DestType Person + права) это колонка с Category Name. По ней показываем карандаш
// правки человека.
function isPersonNameCell(col: LiteColumn): boolean {
  if (!canEditPerson) return false;
  const cell = props.row.cells[col.id];
  if (!cell || !cell.subItems) return false;
  return cell.subItems.some((s) => s.Category === ColumnCategory.Name);
}

function headerPartAt(text: string, idx: number): string {
  if (!text) return "";
  return text.split(",")[idx] || "";
}

// Имя тега (Category Name при DestType Tag) рисует TagNameCellElementLite: текст,
// кнопка правки в углу ЯЧЕЙКИ и инлайн-редактор. Признак держим в isHeavySimple, чтобы
// ячейка не ушла в обычный SimpleCellElementLite, и ловим её до CommonCellElementLite.
function isTagName(cell: TableDataCell): boolean {
  if (cell.DisplayType !== TableDataCellDisplayType.Simple && cell.DisplayType !== TableDataCellDisplayType.SimpleAsTag) {
    return false;
  }
  return (
    cell.Category === ColumnCategory.Name &&
    gstore.selectedItem?.DestType === AnalyticsV2ElementType.Tag
  );
}

// Артикул позиции и движения рисует VendorCodeCellElementLite: текст и кнопки info
// и клонирования. Признак держим в isHeavySimple.
function isVendorCode(cell: TableDataCell): boolean {
  if (cell.DisplayType !== TableDataCellDisplayType.Simple && cell.DisplayType !== TableDataCellDisplayType.SimpleAsTag) {
    return false;
  }
  return (
    cell.Category === ColumnCategory.ItemVendorCode ||
    cell.Category === ColumnCategory.MovementVendorCode
  );
}

// Комментарий позиции и движения склада рисует NotesCellElementLite: NativeInput и
// кнопки шаблонов и правки. Признак держим в isHeavySimple.
function isNote(cell: TableDataCell): boolean {
  if (cell.DisplayType !== TableDataCellDisplayType.Simple && cell.DisplayType !== TableDataCellDisplayType.SimpleAsTag) {
    return false;
  }
  const dest = gstore.selectedItem?.DestType;
  return (
    cell.Category === ColumnCategory.Details &&
    (dest === AnalyticsV2ElementType.StorageItem || dest === AnalyticsV2ElementType.StorageItemMovement)
  );
}

// Табличный комментарий рисуется той же ячейкой, что и складское инфо, но доступен
// в любом сегменте, поэтому проверка только по категории.
function isTableComment(cell: TableDataCell): boolean {
  if (cell.DisplayType !== TableDataCellDisplayType.Simple && cell.DisplayType !== TableDataCellDisplayType.SimpleAsTag) {
    return false;
  }
  return cell.Category === ColumnCategory.TableComments;
}

function isHeavySimple(cell: TableDataCell): boolean {
  if (cell.DisplayType !== TableDataCellDisplayType.Simple && cell.DisplayType !== TableDataCellDisplayType.SimpleAsTag) {
    return false;
  }
  const dest = gstore.selectedItem?.DestType;
  if (cell.Category === ColumnCategory.Name && dest === AnalyticsV2ElementType.Tag) return true;
  if (cell.Category === ColumnCategory.ItemVendorCode || cell.Category === ColumnCategory.MovementVendorCode) return true;
  if (cell.Category === ColumnCategory.Details &&
    (dest === AnalyticsV2ElementType.StorageItem || dest === AnalyticsV2ElementType.StorageItemMovement)) return true;
  if (cell.Category === ColumnCategory.TableComments) return true;
  return false;
}

// Свёрнутая ячейка (col.Minimized), кроме параметров и типа 16: к ним сворачивание
// не применяется. Контент оборачивается в MinimizedCellLite.
function isMinimized(cell: TableDataCell): boolean {
  return (
    !!(cell as any).Minimized &&
    cell.DisplayType !== TableDataCellDisplayType.Parameter &&
    cell.DisplayType !== TableDataCellDisplayType.DataOfStorageItem
  );
}

function iconHtml(cell: TableDataCell): string {
  return formatIconHtml(cell).join("<br/>");
}
</script>

<style lang="scss" scoped>
.qtl-cell {
  padding: 4px;
  vertical-align: top;
  border: 0;
  border-right: 1px solid #D2D2D2;
  border-bottom: 1px solid #D2D2D2;
  background: inherit;
  word-break: normal;
  overflow-wrap: break-word;
  overflow: hidden;
  min-width: 50px;
  position: relative;
}

.qtl-cell--note {
  padding: 0;
}

.qtl-cell--note:has(.qtl-note--editable) {
  cursor: text;
}

.qtl-cell--note:hover :deep(.qtl-note__btn) {
  visibility: visible;
  opacity: 1;
}

.qtl-cell > * {
  max-width: 100%;
  min-width: 0;
}

.qtl-cell:last-of-type {
  border-right: none;
}

.qtl-cell :deep(.no-break) {
  white-space: nowrap;
}

.qtl-cell--multiedit {
  width: 55px;
  min-width: 55px;
  padding: 4px;
  vertical-align: top;
  text-align: left;
}

.qtl-multi {
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 4px;
  flex-wrap: wrap;
}

.qtl-row-num {
  flex: 0 0 auto;
  align-self: center;
  white-space: nowrap;
  font-family: Roboto;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  color: var(--Text-color-text-secondary, #979797);
}

.qtl-multi__check {
  margin: 2px;
}

.qtl-person-edit-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  vertical-align: middle;
  margin-left: 4px;
  border: 0;
  background: transparent;
  color: #979797;
  cursor: pointer;
  line-height: 0;
  padding: 0;
  opacity: 0;
  pointer-events: none;
}

.qtl-person-edit-btn svg {
  display: block;
}

.qtl-row:hover .qtl-person-edit-btn {
  opacity: 1;
  pointer-events: auto;
}

.qtl-row--hover .qtl-person-edit-btn {
  opacity: 1;
  pointer-events: auto;
}

.qtl-person-edit-btn:hover {
  color: #3F884F;
}

.qtl-multi__del {
  flex: 0 0 auto;
  align-self: center;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  background: transparent;
  color: #616161;
  cursor: pointer;
  line-height: 0;
  padding: 0;
  opacity: 0;
  pointer-events: none;
}

/* В svg цвет обводки зашит, поэтому подключаем файл маской и красим фоном */
.qtl-multi__del-ico {
  width: 16px;
  height: 16px;
  display: block;
  background-color: #979797;
  mask: url("@/assets/icon-delete.svg") no-repeat center / contain;
  -webkit-mask: url("@/assets/icon-delete.svg") no-repeat center / contain;
}

.qtl-multi__del:hover .qtl-multi__del-ico {
  background-color: #D05A4F;
}

.qtl-row:hover .qtl-multi__del {
  opacity: 1;
  pointer-events: auto;
}

.qtl-row--hover .qtl-multi__del {
  opacity: 1;
  pointer-events: auto;
}

.qtl-multi__del:disabled {
  color: #cfcfcf;
  cursor: default;
}

.qtl-del-confirm {
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  padding: 10px 12px;
  min-width: 220px;
}

.qtl-del-confirm__text {
  font-size: 13px;
  margin-bottom: 8px;
}

.qtl-del-confirm__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.qtl-del-confirm__btn {
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 13px;
  padding: 4px 8px;
  border-radius: 3px;
  color: #333;
}

.qtl-del-confirm__btn:hover {
  background: #f2f2f2;
}

.qtl-del-confirm__btn--accent {
  color: #C0392B;
  font-weight: 500;
}

.qtl-sub-header {
  display: block;
  max-width: 100%;
  overflow-wrap: anywhere;
  word-break: normal;
  font-weight: 500;
  font-size: 12px;
  color: #333333;
  margin-bottom: 2px;
}

.qtl-icon-cell {
  display: inline;
}

.qtl-cell :deep(.native-input),
.qtl-cell :deep(.native-input-border) {
  margin: 2px;
}

.qtl-cell--note :deep(.native-input) {
  margin: 0;
}
</style>
